import { z } from "zod";

export const generationStatusSchema = z.enum(["pending", "processing", "completed", "failed"]);
export type GenerationStatus = z.infer<typeof generationStatusSchema>;

export const generationTypeSchema = z.enum([
  "ad",
  "edit",
  "director",
  "profile_photo",
  "product_creative",
  "talking_avatar",
  "baseball_trend",
]);
export type GenerationType = z.infer<typeof generationTypeSchema>;

export const sseEventTypeSchema = z.enum(["step", "image", "done", "error"]);
export type SSEEventType = z.infer<typeof sseEventTypeSchema>;

export const creditOperationSchema = z.enum([
  "image_generate",
  "image_edit",
  "video_generate",
  "director_generate",
  // Bot Builder — one-time bot creation (URL scrape + Gemini extraction).
  "bot_create",
  // Bot Builder — owner deposits credits from their main wallet into the
  // bot's prepaid wallet. The pack variant (small/medium/large/xl) picks the
  // amount. Visitor messages don't appear in this enum: they're paid from
  // the bot's wallet via decrement_bot_balance, not from main credits.
  "bot_pack",
]);
export type CreditOperation = z.infer<typeof creditOperationSchema>;

export const userRoleSchema = z.enum(["user", "admin"]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const INSUFFICIENT_CREDITS_CODE = "INSUFFICIENT_CREDITS" as const;
export const CONTENT_FLAGGED_CODE = "CONTENT_FLAGGED" as const;
/** Upstream (Gemini / OpenAI vision) couldn't decode the user's photo — the
 *  user should retry with a different image. Server surface is
 *  `createHttpError("…", 400, INVALID_IMAGE_CODE)`. Frontend matches on
 *  `err.code === INVALID_IMAGE_CODE` to show a localized "we couldn't read
 *  your photo" message instead of the raw upstream error. */
export const INVALID_IMAGE_CODE = "INVALID_IMAGE" as const;
/** Upstream provider timed out on a heavy (e.g. 4K) request. The frontend
 *  branches on this code to show a "try a lower resolution" message. */
export const GENERATION_TIMEOUT_CODE = "GENERATION_TIMEOUT" as const;
/** New purchases are closed during the controlled wind-down. Server surface is
 *  createHttpError("…", 403, PAYMENTS_CLOSED_CODE) from createPaymentController.
 *  Frontend matches `err.code` to show a "purchases are closed" message. */
export const PAYMENTS_CLOSED_CODE = "PAYMENTS_CLOSED" as const;
/** Generation is restricted to active paid subscribers during the wind-down.
 *  Server surface is a 403 with this code from the subscriberOnly middleware.
 *  Frontend matches `err.code` to show the wind-down notice. */
export const SERVICE_WINDING_DOWN_CODE = "SERVICE_WINDING_DOWN" as const;

/**
 * Dev-only model mode. Carried client→server on this header so a dev can flip
 * every flow between the real models and the cheap override models (see
 * config/devModels.ts). Header name + values are a shared contract — a rename
 * on one side would silently break the link with no loud error, so they live
 * here and are imported by both sides. Honored ONLY when the server is
 * non-production; production ignores the header entirely.
 */
export const DEV_MODEL_MODE_HEADER = "x-dev-model-mode" as const;

export const DevModelMode = {
  CHEAP: "cheap",
  NORMAL: "normal",
} as const;
export type DevModelMode = (typeof DevModelMode)[keyof typeof DevModelMode];

export const AD_IMAGES_PER_GENERATION = 4 as const;

/** Event types recorded in the subscription_events table. */
export const SubscriptionEventType = {
  SUBSCRIPTION_CREATED: "subscription_created",
  SUBSCRIPTION_UPGRADED: "subscription_upgraded",
  SUBSCRIPTION_DOWNGRADED: "subscription_downgraded",
  SUBSCRIPTION_RENEWED: "subscription_renewed",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  SUBSCRIPTION_EXPIRED: "subscription_expired",
  SUBSCRIPTION_REACTIVATED: "subscription_reactivated",
  SUBSCRIPTION_DOWNGRADED_TO_FREE: "subscription_downgraded_to_free",
  DOWNGRADE_SCHEDULED: "downgrade_scheduled",
  DOWNGRADE_SCHEDULE_CANCELLED: "downgrade_schedule_cancelled",
  TOKEN_PACK_PURCHASED: "token_pack_purchased",
  CREDITS_REFRESHED: "credits_refreshed",
  DISCOUNT_APPLIED: "discount_applied",
  DISCOUNT_RESTORED: "discount_restored",
} as const;
